import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState
} from 'react'
import { FiX } from 'react-icons/fi'

import { Alert } from '@vtex/shoreline'

import { Button } from '~/components/Buttons'
import { FeedbackContext } from '~/components/feedback'
import Loading from '~/components/Loading'
import ThumbProduct from '~/components/ThumbProduct'
import { getImageDimensions, getVideoDimensions } from '~/helpers'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import Image from '~/modules/retailMedia/components/BannersTable/TumbProduct'
import { campaign as service } from '~/modules/retailMedia/services/industry'

import {
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  MIN_DURATION_SECONDS
} from '../Video'

import * as S from './styles'

type FileToUploadProps = {
  data: { file: File; id: string }
  onSave(data: ItemBannerTable): void
  onDelete(id: ItemBannerTable): void
  getPlacementByDimension(data: Dimension): SizeBySegmentation
  onError?(hasError: boolean): void
  // Own-context size limit override (FR-021e): defaults to Video's constants
  // below so existing callers (Video, AdditionalAssets) keep today's
  // behavior unchanged. Banner passes its own value, derived from the
  // offsite channel, instead of inheriting Video's by import coupling.
  maxFileSizeBytes?: number
  maxFileSizeLabel?: string
}

const FileToUpload: React.FC<FileToUploadProps> = ({
  data: { file: data, id },
  getPlacementByDimension,
  onDelete,
  onSave,
  onError,
  maxFileSizeBytes = MAX_FILE_SIZE_BYTES,
  maxFileSizeLabel = `${MAX_FILE_SIZE_MB}MB`
}) => {
  const { t, tCapitalize } = useAPPTranslation()
  const { toast } = useContext(FeedbackContext)

  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [dimensions, setDimensions] = useState<VideoProps | Dimension>()

  const previewURL = useMemo(() => URL.createObjectURL(data), [data])
  const mediaType = useMemo(() => data?.type, [data])
  const fileSize = useMemo(() => data?.size, [data])
  const isVideo = useMemo(() => mediaType === 'video/mp4', [mediaType])

  const getDimensions = useCallback(async () => {
    const action =
      mediaType === 'video/mp4' ? getVideoDimensions : getImageDimensions

    const imageDimensions = await action(previewURL)

    setDimensions(imageDimensions)
  }, [mediaType, previewURL])

  useLayoutEffect(() => {
    getDimensions()
  }, [getDimensions])

  const placement = useMemo(() => {
    if (!dimensions) {
      return null
    }

    const response = getPlacementByDimension(dimensions)

    if (!response) {
      return null
    }

    return {
      ...response,
      adSize: response.name,
      name: response.size,
      raw_dimensions: dimensions
    }
  }, [getPlacementByDimension, dimensions])

  const isSizeValid = useMemo(
    () => fileSize <= maxFileSizeBytes,
    [fileSize, maxFileSizeBytes]
  )
  const isDurationValid = useMemo(() => {
    if (!isVideo || !dimensions?.duration) {
      return true
    }

    return dimensions.duration >= MIN_DURATION_SECONDS
  }, [isVideo, dimensions])

  const isValid = useMemo(
    () => !!placement && isSizeValid && isDurationValid,
    [placement, isSizeValid, isDurationValid]
  )

  /**
   * Handle upload
   */

  const handleUpload = useCallback(async () => {
    const dataMultipart = new FormData()
    dataMultipart.append('file', data)

    Object.keys(data).forEach(key => dataMultipart.append(key, data[key]))

    setUploading(true)

    const options = {
      onUploadProgress: progressEvent => {
        const progress = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        )

        setUploadProgress(progress)

        console.log(t('rm:progressUploadMedia', { progress }))
      }
    }

    try {
      const action = isVideo ? service.uploadVideo : service.upload

      const response = await action({
        body: dataMultipart,
        axiosOptions: options
      })

      const formatBodyVideo: () => ItemMediaTable = () => {
        const { duration } = dimensions as VideoProps
        return {
          id,
          media_url: response.data.url,
          fileMedia: data,
          ad_size_name: placement.name,
          ad_size: placement.adSize,
          file_size: fileSize,
          dimensions: {
            height: placement.height,
            width: placement.width,
            duration: Math.floor(duration)
          }
        }
      }

      const formatBodyImage: () => ItemBannerTable = () => ({
        id,
        media_url: response.data.url,
        fileMedia: data,
        ad_size_name: placement.name,
        ad_size: placement.adSize,
        raw_dimensions: placement.raw_dimensions,
        dimensions: {
          height: placement.height,
          width: placement.width
        }
      })

      const bodyRequest = isVideo ? formatBodyVideo() : formatBodyImage()

      onSave(bodyRequest)
    } catch (error) {
      console.error(error)
      toast.critical(t('rm:campaign.create.FileToUpload.error.message'))
    }
  }, [data, isVideo, onSave, id, placement, t])

  useEffect(() => {
    if (!isSizeValid) {
      toast.critical(
        t('rm:campaign.create.FileToUpload.fileSize.error.message', {
          size: maxFileSizeLabel
        })
      )

      onError?.(true)
      return
    }

    if (!isDurationValid) {
      toast.critical(
        t('rm:campaign.create.FileToUpload.duration.error.message', {
          duration: MIN_DURATION_SECONDS
        })
      )

      onError?.(true)
      return
    }

    onError?.(false)
    if (placement && !uploading) handleUpload()
  }, [
    isSizeValid,
    isDurationValid,
    placement,
    uploading,
    handleUpload,
    onError,
    maxFileSizeLabel,
    t
  ])

  return (
    <>
      {!isSizeValid && (
        <Alert variant="critical">
          {t('rm:campaign.create.FileToUpload.fileSize.error.message', {
            size: maxFileSizeLabel
          })}
        </Alert>
      )}

      {!isDurationValid && (
        <Alert variant="critical">
          {t('rm:campaign.create.FileToUpload.duration.error.message', {
            duration: MIN_DURATION_SECONDS
          })}
        </Alert>
      )}

      <S.Container isValid={isValid}>
        {isVideo ? (
          <video
            style={{ width: '120px', marginRight: '.5rem' }}
            autoPlay
            muted
          >
            <source src={previewURL} type="video/mp4" />
          </video>
        ) : (
          <Image url={previewURL}>
            <ThumbProduct url={previewURL} />
          </Image>
        )}

        <div className="info">
          <strong>
            {tCapitalize('rm:size')}:{' '}
            <code>
              {placement?.name ||
                t('rm:campaign.create.FileToUpload.anyCompatible')}
            </code>
          </strong>

          <small className="title">{data.name}</small>

          <small className="dimensions">
            {t('rm:campaign.create.FileToUpload.dimensions')}:{' '}
            {dimensions?.width}
            px x {dimensions?.height}px
          </small>

          {dimensions?.duration && (
            <small className="duration">
              {t('rm:campaign.create.FileToUpload.duration')}:{' '}
              {t('c:words.secondCount', {
                count: Math.floor(dimensions.duration)
              })}
            </small>
          )}

          <small className="file-size">
            {t('rm:campaign.create.FileToUpload.fileSize')}:{' '}
            {Math.floor(fileSize / 1024 / 1024)} MB
          </small>
        </div>

        <div className="d-flex align-items-center action me-2">
          <Loading status={uploading}>
            {t('rm:campaign.create.FileToUpload.uploadingMedia')}{' '}
            {uploadProgress}%
          </Loading>

          {!isValid && (
            <Button
              template="critical"
              text={t('c:actions.remove')}
              onClick={onDelete}
              iconLeft={<FiX />}
              customWidth="auto"
              size="small"
            />
          )}
        </div>
      </S.Container>
    </>
  )
}

export default FileToUpload
